import { paidCreationPaths, useBillingActionModalStore, type SubscriptionRequirement } from "./vend/state/billingActionModal";
import { formatPhone, normalizePhone } from "@vendistri/ui";
import React from "react";
// custom imports
import {
  ApiResponseError,
  errorMessageFromResponse,
  jsonFromResponse,
} from "./apiError";
import { useToastStore } from "./vend/state/toast";
import {
  getSuperuserRequestHeaders,
  getSuperuserSupportContextKey,
  useSuperuserContextStore,
} from "./vend/state/superuserContext";
import type { BillingStatus } from "./user/subscription";
import { getAccountContextRequestHeaders, selectedAccountContext, subscribeToAccountContextChange } from "./vend/state/accountContext";

// third party
import { useState } from "react";
import { MdCheckCircle, MdOutlineError } from "react-icons/md";
import { setCsrfToken } from "./user/api";

export { formatPhone, normalizePhone };

export const SUPPORTED_MESH_FORMATS = ".glb,.obj,.stl,.3mf,.ply,.gltf,.fbx";

export function normalizeIdentityEmail(email?: string | null): string {
  return (email || "").trim().toLowerCase();
}

// theme utils
export const getPropValue = (prop: string) =>
  window.getComputedStyle(document.documentElement).getPropertyValue(prop);

// state management utils
export function useCustomState<T>(
  initialState: any
): [T, (newState: any) => any] {
  const [state, setState] = useState(initialState);
  const setCustomSate = (newState: any) => {
    setState((prevState: any) => ({ ...prevState, ...newState }));
  };

  return [state, setCustomSate];
}

export function constructUrl(url: string, args: Object) {
  if (!args) return url;

  url += "?";
  for (const [arg, value] of Object.entries(args)) {
    url += `${arg}=${value}&`;
  }

  return url;
}

// safer URL builder with encoding
export function buildUrl(
  base: string,
  params?: Record<string, string | number | boolean | null | undefined>
): string {
  if (!params || Object.keys(params).length === 0) return base;
  const url = new URL(base, window.location.origin);
  Object.entries(params).forEach(([key, val]) => {
    if (val === undefined || val === null) return;
    url.searchParams.append(key, String(val));
  });
  return url.toString();
}

// local storage utils
export function addToLocalStorage(key: string, value: string) {
  // console.log(`[addToLocalStorage] >> adding ${key}...`)
  try {
    localStorage.setItem(key, value);
  } catch (error) {
    clearLocalStorage();
    localStorage.setItem(key, value);
  }
}

export function clearLocalStorage() {
  console.log(`[clearLocalStorage] >> clearing...`);
  const permanent: string[] = [];
  const n = localStorage.length;

  for (let i = 0; i < n; i++) {
    const key = localStorage.key(i);
    if (!key) continue;
    if (permanent.includes(key)) continue;
    localStorage.removeItem(key);
  }
}

// array utils
export function get(a: any, key: string | number): any {
  if (!a) return;
  if (typeof key === "number") return a[key];
  return key.split(".").reduce((curr, key) => curr && curr[key], a);
}

export function equals(
  a: any,
  b: any,
  keys: (string | number)[],
  bKeys?: (string | number)[],
  check = (a: boolean, b: boolean) => a && b,
  defaultCheck = true
): boolean {
  return keys.reduce((curr, key, i) => {
    const a_i = get(a, key);
    const b_i = get(b, (bKeys && bKeys[i]) || key);

    let equal = false;

    if (Array.isArray(a_i) && Array.isArray(b_i)) {
      equal = equals(
        a_i,
        b_i,
        Array(a_i.length)
          .fill(0)
          .map((_, i) => i)
      );
    } else {
      equal = a_i === b_i;
    }

    return check(curr, equal);
  }, defaultCheck);
}

export function arrEquals(
  A: any[],
  B: any[],
  keys: string[],
  bKeys?: string[],
  check = (a: boolean, b: boolean) => a && b,
  defaultCheck = true
): boolean {
  if (A.length !== B.length) return false;
  return A.reduce(
    (curr, a, i) => check(curr, equals(a, B[i], keys, bKeys)),
    defaultCheck
  );
}

export function find<T>(
  A: any[],
  a: any,
  keys: string[],
  aKeys?: string[]
): T | undefined {
  return A.find((Ai) => equals(Ai, a, keys, aKeys));
}

export function findAll<T>(
  A: any[],
  a: any,
  keys: string[],
  aKeys?: string[],
  check = (eq: boolean) => eq
): T[] {
  return A.filter((Ai) => check(equals(Ai, a, keys, aKeys)));
}

export function insert<T>(A: T[], B: T[], i: number): T[] {
  return [...A.slice(0, i), ...B, ...A.slice(i, A.length)];
}


/**
 * Filters an array based on the comparison of specified object properties.
 *
 * @param A - The array to filter.
 * @param a - The object to compare each element against.
 * @param keys - The keys of the properties to compare.
 * @param aKeys - Optional. The keys of the object `a` to compare against `keys`.
 * @param check - A function to determine which elements to include based on the comparison result, defaults to `!eq`.
 * @returns A new array containing elements for which the `check` function returns true.
 */
export function filter<T>(
  A: any[],
  a: any | any[],
  keys: string[],
  aKeys?: string[],
  check = (eq: boolean) => !eq
): T[] {
  return A.filter((Ai) => {
    if (a instanceof Array) {
      return check(find(a, Ai, keys, aKeys) !== undefined);
    }
    return check(equals(Ai, a, keys, aKeys));
  }) as T[];
}

export function any<T>(
  A: T[],
  B: T[],
  keys: string[],
  check = (eq: boolean) => eq
): boolean {
  return A.reduce(
    (curr, a) =>
      curr || B.reduce((curr, b) => curr || check(equals(a, b, keys)), false),
    false
  );
}

export function map<T>(A: T[], mapFn: (a: T) => any, defaultValue?: T): T[] {
  const B: T[] = [];

  let a = defaultValue;
  for (let i = 0; i < A.length; i++) {
    a = mapFn(A[i]) || a;
    if (a) B.push(a);
  }
  return B;
}

// object utils
export function filterObj<T>(
  A: any,
  removeKeys: string[] = [],
  setKeys: any = {}
): T {
  const B: any = { ...A };
  for (const key of removeKeys) delete B[key];

  for (const [key, value] of Object.entries(setKeys)) {
    B[key] = A[key] || value;
  }

  return B as T;
}

export function update<T>(
  A: any[],
  a: any | any[] | ((a: any) => boolean),
  keys: string[],
  updates: any,
  aKeys?: string[],
  check?: (a: boolean, b: boolean) => boolean,
  defaultCheck?: boolean
): T[] {
  return A.map((Ai) => {
    let found = false;
    if (a instanceof Array) {
      const ai = find(a, Ai, keys, aKeys);
      if (ai) {
        found = true;
      }
    } else if (typeof a === "function") {
      if (a(Ai)) {
        found = true;
      }
    } else if (a instanceof Object) {
      if (equals(Ai, a, keys, aKeys, check, defaultCheck)) {
        found = true;
      }
    } else {
      throw new Error("Invalid argument type");
    }

    if (found) {
      const Aii = { ...Ai };
      for (const [key, value] of Object.entries(updates)) {
        switch (typeof value) {
          case "object":
            Aii[key] = { ...Aii[key], ...value };
            break;
          default:
            Aii[key] = value;
        }
      }
      return Aii;
    }
    return Ai;
  });
}

// authentication utils
export function getCookie(name: string): string {
  const value = "; " + document.cookie;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop()!.split(";").shift()!;

  return "";
}

export function getCsrfToken(): string {
  const cookieToken = getCookie("csrftoken");
  if (cookieToken) return cookieToken;
  try {
    return window.localStorage.getItem("csrftoken") || "";
  } catch (error) {
    console.error(error);
    return "";
  }
}

export async function defaultFetchHeaders(
  contentType: string | null = "application/json",
  { ...headers }: HeadersInit = {}
): Promise<HeadersInit> {
  return {
    ...(contentType ? { "Content-Type": contentType } : {}),
    "X-CSRFToken": getCsrfToken() || await setCsrfToken(),
    ...defaultReadHeaders(headers),
  };
}

export function defaultReadHeaders(
  { ...headers }: HeadersInit = {}
): HeadersInit {
  return {
    ...(getSuperuserSupportContextKey() === "account" ? getAccountContextRequestHeaders() : {}),
    ...getSuperuserRequestHeaders(),
    ...headers,
  };
}

export class PaymentRequiredError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "PaymentRequiredError";
  }
}

export function isPastDueSubscription(
  billingStatus?: BillingStatus | null
): boolean {
  return billingStatus?.subscriptionState === "past_due";
}

export function presentPaymentRequired(
  message = "Add a payment method to continue."
): PaymentRequiredError {
  const now = Date.now();
  if (now - lastPaymentToastAt > PAYMENT_TOAST_THROTTLE_MS) {
    useToastStore.getState().show({
      title: "Payment Required",
      message,
      status: "error",
    });
    lastPaymentToastAt = now;
  }
  if (now - lastPaymentRedirectAt > PAYMENT_REDIRECT_THROTTLE_MS) {
    window.setTimeout(() => {
      navigateToPaymentMethods();
    }, PAYMENT_REDIRECT_DELAY_MS);
    lastPaymentRedirectAt = now;
  }
  return new PaymentRequiredError(message);
}

export function assertBillingWriteAllowed(
  billingStatus?: BillingStatus | null,
  message = "Add a payment method to continue."
): void {
  if (!isPastDueSubscription(billingStatus)) return;
  throw presentPaymentRequired(message);
}

export async function handlePaymentRequired(
  res: Response,
  errorContext = "Request failed"
): Promise<PaymentRequiredError> {
  const message = await errorMessageFromResponse(res, errorContext);
  return presentPaymentRequired(message);
}

const creationOperationIdPattern = /^[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}$/i;

function isSubscriptionRequirement(value: unknown): value is SubscriptionRequirement {
  if (!value || typeof value !== "object") return false;
  const row = value as Record<string, unknown>;
  return row.code === "subscription_required" && typeof row.action === "string" &&
    Object.prototype.hasOwnProperty.call(paidCreationPaths, row.action) &&
    typeof row.actionLabel === "string" && typeof row.organizationId === "string" &&
    typeof row.operationId === "string" && creationOperationIdPattern.test(row.operationId);
}

export async function fetchJson<T>(
  url: string,
  init: RequestInit,
  errorContext = "Request failed"
): Promise<T> {
  const creationAction = init.method?.toUpperCase() === "POST"
    ? (Object.keys(paidCreationPaths) as Array<keyof typeof paidCreationPaths>).find(action =>
      new URL(url, window.location.origin).href === new URL(
        `${process.env.REACT_APP_BACKEND_URL || ""}${paidCreationPaths[action]}`, window.location.origin,
      ).href)
    : undefined;
  const creation = creationAction !== undefined;
  if (creation) init = { ...init, headers: new Headers(init.headers) };
  let creationOperationId: string | undefined;
  if (creation && typeof init.body === "string") {
    try {
      const body = JSON.parse(init.body);
      if (typeof body?.operationId === "string" && creationOperationIdPattern.test(body.operationId)) creationOperationId = body.operationId;
    } catch { /* Older or unsupported requests retain their existing failure behavior. */ }
  }
  const identifiedCreation = creationOperationId !== undefined;
  let uncertainResponse = false;
  const controller = new AbortController();
  registerAbortController(controller);
  const abort = () => controller.abort();
  init.signal?.addEventListener("abort", abort, { once: true });
  if (init.signal?.aborted) abort();
  const unsubscribe = creation ? subscribeToAccountContextChange(abort) : undefined;
  const scope = JSON.stringify([defaultReadHeaders(), selectedAccountContext()?.id]);
  const requireCurrentCreation = () => {
    if (creation && (controller.signal.aborted || JSON.stringify([defaultReadHeaders(), selectedAccountContext()?.id]) !== scope)) {
      throw new DOMException("The account context changed before saving.", "AbortError");
    }
  };
  try {
    const signal = controller.signal;
    const send = async () => {
      uncertainResponse = identifiedCreation;
      const response = await fetch(url, { ...init, signal });
      uncertainResponse = identifiedCreation && response.status >= 500;
      requireCurrentCreation();
      return response;
    };
    let res = await send();
    requireCurrentCreation();
    if (res.status === 402 && init.method?.toUpperCase() === "POST" && typeof init.body === "string") {
      const requirement: unknown = await res.clone().json().catch(() => null);
      if (isSubscriptionRequirement(requirement) && requirement.action === creationAction) {
        if (creationOperationId === requirement.operationId) {
          requireCurrentCreation();
          if (selectedAccountContext()?.organizationId !== requirement.organizationId) {
            throw new DOMException("The subscription belongs to another account context.", "AbortError");
          }
          const confirmed = await useBillingActionModalStore.getState().requestSubscription(requirement, signal);
          requireCurrentCreation();
          if (!confirmed) throw new PaymentRequiredError("Subscription was not completed. Your form is still available.");
          // Replay only this audited creation, once, with its original durable operation ID.
          res = await send();
          requireCurrentCreation();
          if (res.status === 402) throw new PaymentRequiredError(await errorMessageFromResponse(res, errorContext));
        }
      }
    }
    if (!res.ok) {
      if (res.status === 402) {
        throw await handlePaymentRequired(res, errorContext);
      }
      if (res.status === 401 && shouldSuppressUnauthorized()) {
        return null as T;
      }
      const message = await errorMessageFromResponse(res, errorContext);
      if (res.status === 403) {
        presentSuperuserAccessError(message);
      }
      throw new ApiResponseError(message, res.status);
    }
    uncertainResponse = identifiedCreation;
    const result = await jsonFromResponse<T>(res);
    requireCurrentCreation();
    return result;
  } catch (error: any) {
    requireCurrentCreation();
    if (error?.name === "AbortError") {
      throw error;
    }
    if (uncertainResponse && !controller.signal.aborted) {
      // A read-only receipt check reuses all endpoint authorization/validation.
      // It must never allocate a receipt or execute the creation callback.
      // A separate path fails closed against older servers instead of replaying a mutation.
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || ""}/user/creation-check/${creationAction}`, { ...init, signal: controller.signal });
        requireCurrentCreation();
        if (!response.ok) {
          const message = await errorMessageFromResponse(response, "The save outcome could not be checked. Keep this form and retry the same save.");
          requireCurrentCreation();
          if (response.status === 403) presentSuperuserAccessError(message);
          throw new ApiResponseError(message, response.status);
        }
        const result = await jsonFromResponse<T>(response);
        requireCurrentCreation();
        return result;
      } catch (checkError) {
        requireCurrentCreation();
        throw checkError;
      }
    }
    throw error;
  } finally {
    unsubscribe?.();
    init.signal?.removeEventListener("abort", abort);
    unregisterAbortController(controller);
  }
}

function presentSuperuserAccessError(message: string): void {
  const state = useSuperuserContextStore.getState();
  if (!state.enabled || !state.selectedOrg) return;
  const lowered = message.toLowerCase();
  if (lowered.includes("superuser organization write access is not enabled")) {
    state.requestWriteAttention();
    useToastStore.getState().show({
      title: "Write Mode Required",
      message: "Enable write mode before saving changes.",
      status: "error",
    });
    return;
  }
  if (lowered.includes("superuser billing access is not enabled")) {
    state.requestBillingAttention();
    useToastStore.getState().show({
      title: "Billing Mode Required",
      message: "Enable billing mode before using billing support.",
      status: "error",
    });
  }
}

const PAYMENT_REDIRECT_THROTTLE_MS = 2500;
const PAYMENT_REDIRECT_DELAY_MS = 1000;
const PAYMENT_TOAST_THROTTLE_MS = 1500;
let lastPaymentRedirectAt = 0;
let lastPaymentToastAt = 0;

function navigateToPaymentMethods(): void {
  const target = "/account-settings?tab=payment-methods&open=card";
  if (window.location.pathname + window.location.search === target) return;
  window.history.pushState({}, "", target);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

let suppressUnauthorizedUntil = 0;

export function suppressUnauthorizedFor(ms = 1500): void {
  suppressUnauthorizedUntil = Date.now() + ms;
}

const shouldSuppressUnauthorized = (): boolean =>
  Date.now() < suppressUnauthorizedUntil;

const activeControllers = new Set<AbortController>();

export function registerAbortController(controller: AbortController): void {
  activeControllers.add(controller);
}

export function unregisterAbortController(controller: AbortController): void {
  activeControllers.delete(controller);
}

export function abortAllRequests(): void {
  activeControllers.forEach((controller) => controller.abort());
  activeControllers.clear();
}

export function elapsedTime(start: Date): string {
  const end = new Date();
  const diff = end.getTime() - start.getTime();

  const seconds = Math.floor(diff / 1000);
  const mins = Math.floor(seconds / 60);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);

  let elapsed = "";
  if (days > 0) {
    elapsed += `${days} days`;
  } else if (hours > 0) {
    elapsed += `${hours} hours`;
  } else if (mins > 0) {
    elapsed += `${mins} minutes`;
  } else {
    elapsed += `${seconds} seconds`;
  }

  return elapsed;
}

// Error display utility for consistent error rendering
export function ErrorMessage({
  message,
}: {
  message?: string | null;
}): React.ReactElement | null {
  if (!message) return null;
  const Icon = MdOutlineError as unknown as React.ElementType;
  return React.createElement(
    "div",
    { className: "flex items-center gap-2 text-red-500 text-sm" },
    React.createElement(Icon, { className: "h-4 w-4" }),
    React.createElement("span", null, message)
  );
}

export function SuccessMessage({
  message,
}: {
  message?: string | null;
}): React.ReactElement | null {
  if (!message) return null;
  const Icon = MdCheckCircle as unknown as React.ElementType;
  return React.createElement(
    "div",
    { className: "flex items-center gap-2 text-emerald-600 text-sm" },
    React.createElement(Icon, { className: "h-4 w-4" }),
    React.createElement("span", null, message)
  );
}

export function camelCaseToString(a: string): string {
  return a
    .replace(/([A-Z])/g, " $1") // Add space before uppercase letters
    .replace(/^./, (match) => match.toUpperCase()) // Capitalize the first letter
    .trim();
}

export function trimmedOrEmpty(value?: string | null) {
  return (value || "").trim();
}


export function smoothScrollToElement(
  el: HTMLElement,
  options: { durationMs?: number; block?: ScrollLogicalPosition } = {}
) {
  const { durationMs = 500, block = "center" } = options;
  const start = window.scrollY || document.documentElement.scrollTop;
  const rect = el.getBoundingClientRect();
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
  const targetTop =
    start +
    rect.top -
    (block === "center" ? viewportHeight / 2 - rect.height / 2 : 0);
  const distance = targetTop - start;
  const startTime = performance.now();

  const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

  const step = (now: number) => {
    const elapsed = now - startTime;
    const progress = Math.min(1, elapsed / durationMs);
    const eased = easeOutCubic(progress);
    window.scrollTo(0, start + distance * eased);
    if (progress < 1) {
      requestAnimationFrame(step);
    }
  };

  requestAnimationFrame(step);
}


export function capitalizeField(val?: string | null) {
  if (!val) return "";
  return val
    .replace("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

// render numeric fields safely (avoid "null" / "undefined")
export function displayNumber(val?: number | null) {
  return val === null || val === undefined || Number.isNaN(val) ? "" : `${val}`;
}

export function parseNumericInput(raw: string): number | undefined {
  const trimmed = raw.trim();
  if (trimmed === "") return undefined;
  const next = Number(trimmed);
  return Number.isNaN(next) ? Number.NaN : next;
}

export function formatNumericInput(value?: number | null): string {
  if (value === undefined || value === null) return "";
  if (!Number.isFinite(value)) return "";
  return `${value}`;
}

export function formatNumericInputForSync(value?: number | null): string | null {
  if (value === undefined || value === null) return "";
  if (!Number.isFinite(value)) return null;
  return `${value}`;
}
